import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  DashboardActivitySummaryParams,
  DashboardBreakdownParams,
  DashboardLowBalanceParams,
  DashboardRiskUsersParams,
  DashboardTopCreditConsumersParams,
  DashboardTopMatchesParams,
  getDashboardActivitySummary,
  getDashboardOnlineUsers,
  getDashboardHierarchyBreakdown,
  getDashboardLowBalanceChildren,
  getDashboardRiskUsers,
  getDashboardSportCategories,
  getDashboardTopCreditConsumers,
  getDashboardTopMatches,
} from "@/api/dashboard";

export enum DashboardQueryKeys {
  TopMatches = "dashboard.top-matches",
  RiskUsers = "dashboard.risk-users",
  HierarchyBreakdown = "dashboard.hierarchy-breakdown",
  ActivitySummary = "dashboard.activity-summary",
  LowBalanceChildren = "dashboard.low-balance-children",
  TopCreditConsumers = "dashboard.top-credit-consumers",
  SportCategories = "dashboard.sport-categories",
  OnlineUsers = "dashboard.online-users",
}

// Server caches these aggregates for ~60s; matching refetch cadence keeps the
// widgets fresh without hammering the API.
const DASHBOARD_REFETCH_MS = 60_000;

export const useDashboardTopMatches = (
  params: DashboardTopMatchesParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.TopMatches, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) => getDashboardTopMatches({ signal, params }),
  });
};

export const useDashboardTopCreditConsumers = (
  params: DashboardTopCreditConsumersParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.TopCreditConsumers, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) =>
      getDashboardTopCreditConsumers({ signal, params }),
  });
};

export const useDashboardRiskUsers = (
  params: DashboardRiskUsersParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.RiskUsers, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) => getDashboardRiskUsers({ signal, params }),
  });
};

export const useDashboardActivitySummary = (
  params: DashboardActivitySummaryParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.ActivitySummary, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) => getDashboardActivitySummary({ signal, params }),
  });
};

// Presence changes by the second and the read is a cached O(log N) ZCOUNT,
// so this one polls far faster than the 60s aggregates.
const ONLINE_REFETCH_MS = 15_000;

export const useDashboardOnlineUsers = (
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.OnlineUsers],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: ONLINE_REFETCH_MS,
    queryFn: ({ signal }) => getDashboardOnlineUsers({ signal, params: {} }),
  });
};

export const useDashboardLowBalanceChildren = (
  params: DashboardLowBalanceParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.LowBalanceChildren, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) =>
      getDashboardLowBalanceChildren({ signal, params }),
  });
};

export const useDashboardSportCategories = (
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.SportCategories],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) => getDashboardSportCategories({ signal }),
  });
};

export const useDashboardHierarchyBreakdown = (
  params: DashboardBreakdownParams = {},
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: [DashboardQueryKeys.HierarchyBreakdown, params],
    enabled: options.enabled ?? true,
    placeholderData: keepPreviousData,
    refetchInterval: DASHBOARD_REFETCH_MS,
    queryFn: ({ signal }) =>
      getDashboardHierarchyBreakdown({ signal, params }),
  });
};
